import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {AxiosError} from "axios";
import {TUnavailabilityApiResponse} from "../redux/unavailability/i-unavailability-form";
import {AnalyticsEvent} from "../utils/analytics";
import {useClient, useClientV2} from "../utils/hooks";
import {normalizeV2AvailabilityResponse} from "./availability/availability-normalizer";

export type TAvailabilityApiResponse = {
  sun: {from: string; to: string}[];
  mon: {from: string; to: string}[];
  tue: {from: string; to: string}[];
  wed: {from: string; to: string}[];
  thu: {from: string; to: string}[];
  fri: {from: string; to: string}[];
  sat: {from: string; to: string}[];
};

export type TUnavailabilityRangePayload = {
  from: string;
  to: string;
  /**
   * Server id of an existing range. Present for every range that is already
   * stored, omitted only for ranges the coach just added.
   */
  unavailabilityId?: number;
};

/**
 * `POST booking/unavailability` is a replace-set: the submitted array becomes
 * the stored set outright. Ranges carrying an `unavailabilityId` are updated
 * in place, ranges without one are inserted, and any stored range missing from
 * `selections` is deleted — so `selections: []` clears every range.
 */
export type TUnavailabilityPayload = {
  selections: TUnavailabilityRangePayload[];
};

/**
 * The API client rejects with the raw response body, not an `AxiosError`, and
 * rejects with `undefined` when the request never reached the server.
 */
type TApiErrorBody = {status?: boolean; message?: string | string[]} | undefined;

const UNAVAILABILITY_SAVE_FALLBACK_ERROR =
  "We couldn't save your unavailable dates. Please try again.";

/**
 * Surfaces the server's own message — "Duplicate date ranges detected",
 * "The from date cannot be in the past", "Unavailability N does not exist" —
 * rather than a generic HTTP failure. Nest's validation pipe returns an array
 * of messages, so flatten that case too.
 */
const getUnavailabilityErrorMessage = (error: TApiErrorBody): string => {
  const serverMessage = error?.message;

  if (Array.isArray(serverMessage)) {
    return serverMessage.filter(Boolean).join(", ") || UNAVAILABILITY_SAVE_FALLBACK_ERROR;
  }

  return serverMessage || UNAVAILABILITY_SAVE_FALLBACK_ERROR;
};

const useGetAvailability = () => {
  const client = useClient("AVAILABILITY");

  return useQuery<
    TAvailabilityApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["availability"],
    queryFn: async () => {
      const data = await client("availability");
      const inner = data?.data ?? data;
      return normalizeV2AvailabilityResponse(inner);
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

const useGetUnavailability = () => {
  const clientV2 = useClientV2();

  return useQuery<
    TUnavailabilityApiResponse[],
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["unavailability"],
    queryFn: () => clientV2(`booking/unavailability`).then((data) => data.data),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // The save endpoint replaces the whole stored set, so the form must seed
    // itself from server truth: a cache left over from an earlier visit would
    // omit ranges added elsewhere since, and omission means deletion.
    refetchOnMount: "always",
  });
};

const useUpdateAvailability = () => {
  const queryClient = useQueryClient();
  const client = useClient("AVAILABILITY");

  return useMutation({
    mutationFn: async (payload: TAvailabilityApiResponse) => {
      const res = await client("availability/add", {method: "POST", data: payload});
      const inner = res?.data ?? res;
      return normalizeV2AvailabilityResponse(inner);
    },
    onMutate: (values) => {
      queryClient.cancelQueries(["availability"]);

      const oldAvailability = queryClient.getQueryData(["availability"]);
      queryClient.setQueryData(["availability"], values);

      return () => queryClient.setQueryData(["availability"], oldAvailability);
    },
    onError: (error: AxiosError<{message: string}>, values, rollback) => {
      message.error(error.message);
      AnalyticsEvent("Availability", "Availability Update Fail");
      if (typeof rollback === "function") {
        rollback();
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["availability"], data);
      AnalyticsEvent("Availability", "Availability Update Success");
      message.success("Your availability preferences has been saved successfully");
    },
    onSettled: () => queryClient.invalidateQueries({queryKey: ["availability"]}),
  });
};

/**
 * Saves the coach's complete unavailability set.
 *
 * Deletion happens here, by omission — there is deliberately no separate
 * delete mutation. A second, immediately-applied delete path would race this
 * one: a range dropped by `DELETE` while an older form state is in flight gets
 * re-created, and a range already deleted server-side makes the next save fail
 * outright with "Unavailability N does not exist", losing the coach's other
 * edits with it. One writer, one source of truth.
 */
const useUpdateUnavailability = () => {
  const queryClient = useQueryClient();
  const clientV2 = useClientV2();

  return useMutation<
    {data?: TUnavailabilityApiResponse[]},
    TApiErrorBody,
    TUnavailabilityPayload
  >({
    mutationFn: (payload: TUnavailabilityPayload) =>
      clientV2(`booking/unavailability`, {
        method: "POST",
        data: payload,
      }),
    onError: (error) => {
      message.error(getUnavailabilityErrorMessage(error));
      AnalyticsEvent("Unavailability", "Unavailability Update Fail");
    },
    onSuccess: (response) => {
      // The response is the newly stored set, ids and all. Seed the cache with
      // it so the next save already knows the ids of rows just inserted.
      if (Array.isArray(response?.data)) {
        queryClient.setQueryData(["unavailability"], response.data);
      }
      AnalyticsEvent("Unavailability", "Unavailability Update Success");
    },
    onSettled: () => queryClient.invalidateQueries({queryKey: ["unavailability"]}),
  });
};

const useAddStartTimeFrequency = () => {
  const queryClient = useQueryClient();
  const client = useClient("SETTINGS");
  const endpoint = "booking/settings/buffer-time";
  return useMutation({
    mutationFn: (payload: {bufferTime: number}) =>
      client(endpoint, {
        method: "POST",
        data: payload,
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
      AnalyticsEvent("Buffer time", "Buffer time Update Fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Buffer time", "Buffer time Update Success");
    },
    onSettled: () => queryClient.invalidateQueries({queryKey: ["profile"]}),
  });
};

const useGetMinimumNoticePeriod = () => {
  const client = useClient("BOOKING_SETTINGS");

  return useQuery<{noticePeriod: number}, AxiosError<{status: boolean; message: string}>>(
    {
      queryKey: ["minimum_notice_period"],
      queryFn: () => client(`booking/settings/notice-period`).then((data) => data.data),
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    }
  );
};

const useAddMinimumNoticePeriod = () => {
  const queryClient = useQueryClient();
  const client = useClient("BOOKING_SETTINGS");
  return useMutation({
    mutationFn: (payload: {notice_period: number}) =>
      client(`booking/settings/notice-period`, {
        method: "POST",
        data: {noticePeriod: payload.notice_period},
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
      AnalyticsEvent("Buffer time", "Minimum notice period Update Fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Buffer time", "Minimum notice period Update Success");
    },
    onSettled: () => queryClient.invalidateQueries({queryKey: ["minimum_notice_period"]}),
  });
};

export {
  useGetAvailability,
  useUpdateAvailability,
  useGetUnavailability,
  useUpdateUnavailability,
  useAddMinimumNoticePeriod,
  useGetMinimumNoticePeriod,
  useAddStartTimeFrequency,
};
